/**
 * LeaderMetricsPanel Component
 *
 * Displays leader/team metrics for an employee who manages others:
 * - Team size (direct + indirect)
 * - Team performance (average, distribution)
 * - Team OKRs (completion rate, at-risk)
 * - Feedback health (team ratio, recognition rate)
 * - Engagement (1-on-1 coverage, overall health)
 * - Direct reports list
 */
import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card'
import { Users, AlertTriangle, ArrowRight, Network } from 'lucide-react'
import { RadarCard } from './RadarCard'
import { EngagementIndicator } from './EngagementIndicator'
import { PerformanceDistributionBar } from './PerformanceDistributionChart'
import { useRadarUIStore } from '@/stores/radar-ui-store'
import type { LeaderMetrics, RadarSummary, DirectReportSortBy, SortDirection } from '@/types/radar'

interface LeaderMetricsPanelProps {
  metrics: LeaderMetrics
  directReports: RadarSummary[]
  employeeId: string
  className?: string
}

export function LeaderMetricsPanel({
  metrics,
  directReports,
  employeeId,
  className,
}: LeaderMetricsPanelProps) {
  const navigate = useNavigate()
  const {
    directReportsSortBy,
    directReportsSortDir,
    setDirectReportsSort,
    isFavorite,
    toggleFavorite,
  } = useRadarUIStore()

  // Sort direct reports
  const sortedReports = React.useMemo(() => {
    const sorted = [...directReports].sort((a, b) => {
      let comparison = 0
      switch (directReportsSortBy) {
        case 'name':
          comparison = a.employee.name.localeCompare(b.employee.name)
          break
        case 'score':
          comparison = (b.performanceScore ?? 0) - (a.performanceScore ?? 0)
          break
        case 'teamSize':
          comparison = b.teamSize - a.teamSize
          break
        case 'engagement':
          const signalOrder = { green: 3, yellow: 2, red: 1 }
          comparison = signalOrder[b.engagementSignal] - signalOrder[a.engagementSignal]
          break
      }
      return directReportsSortDir === 'asc' ? -comparison : comparison
    })
    return sorted
  }, [directReports, directReportsSortBy, directReportsSortDir])

  const handleSortChange = (sortBy: DirectReportSortBy) => {
    setDirectReportsSort(sortBy)
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center gap-2 text-brand-gray">
        <Users className="w-5 h-5" />
        <span>
          Como Líder ({metrics.teamSize.total} reportes: {metrics.teamSize.direct} diretos + {metrics.teamSize.total - metrics.teamSize.direct} indiretos)
        </span>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Team Performance */}
        <Card>
          <CardHeader>
            <CardTitle>Performance da Equipe</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-brand-gray">Média</span>
                <span className={cn(
                  'text-2xl font-bold',
                  metrics.performance.avgScore >= 4.5 ? 'text-green-500' :
                  metrics.performance.avgScore >= 3.5 ? 'text-brand-primary' :
                  metrics.performance.avgScore >= 2.5 ? 'text-yellow-500' : 'text-red-500'
                )}>
                  {metrics.performance.avgScore.toFixed(1)} / 5
                </span>
              </div>

              <div>
                <span className="text-sm text-brand-gray">Distribuição</span>
                <PerformanceDistributionBar
                  distribution={metrics.performance.distribution}
                  className="mt-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-brand-gray">Excelente</span>
                  <span className="text-green-500 font-medium">{metrics.performance.distribution.excellent}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-brand-gray">Acima da média</span>
                  <span className="text-brand-primary font-medium">{metrics.performance.distribution.aboveAverage}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-brand-gray">Adequado</span>
                  <span className="text-yellow-500 font-medium">{metrics.performance.distribution.adequate}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-brand-gray">Precisa melhorar</span>
                  <span className="text-red-500 font-medium">{metrics.performance.distribution.needsImprovement}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team OKRs */}
        <Card>
          <CardHeader>
            <CardTitle>OKRs da Equipe</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-brand-gray">Taxa de Conclusão</span>
                <span className={cn(
                  'text-2xl font-bold',
                  metrics.okrs.teamCompletionRate >= 70 ? 'text-green-500' :
                  metrics.okrs.teamCompletionRate >= 50 ? 'text-yellow-500' : 'text-red-500'
                )}>
                  {Math.round(metrics.okrs.teamCompletionRate)}%
                </span>
              </div>
              <div className="h-3 bg-brand-dark-light rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-primary rounded-full transition-all duration-500"
                  style={{ width: `${metrics.okrs.teamCompletionRate}%` }}
                />
              </div>
              {metrics.okrs.atRiskCount > 0 && (
                <div className="flex items-center gap-2 text-yellow-500 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {metrics.okrs.atRiskCount} OKRs em risco
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Feedback Health */}
        <Card>
          <CardHeader>
            <CardTitle>Saúde de Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-brand-gray">Proporção Média</span>
                <span className={cn(
                  'text-xl font-bold',
                  metrics.feedbackHealth.teamRatio >= 1 ? 'text-green-500' : 'text-yellow-500'
                )}>
                  {metrics.feedbackHealth.teamRatio.toFixed(1)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-brand-gray">Taxa de Reconhecimento</span>
                <span className="text-xl font-bold text-white">
                  {Math.round(metrics.feedbackHealth.recognitionRate)}%
                </span>
              </div>
              <div className={cn(
                'flex items-center gap-2 text-sm px-3 py-2 rounded-lg',
                metrics.feedbackHealth.teamRatio >= 1 && metrics.feedbackHealth.recognitionRate >= 50
                  ? 'bg-green-500/10 text-green-500'
                  : 'bg-yellow-500/10 text-yellow-500'
              )}>
                {metrics.feedbackHealth.teamRatio >= 1 && metrics.feedbackHealth.recognitionRate >= 50
                  ? 'Cultura de feedback saudável'
                  : 'Atenção à cultura de feedback'}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Engagement */}
        <Card>
          <CardHeader>
            <CardTitle>Engajamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-brand-gray">Cobertura 1-on-1</span>
                <span className={cn(
                  'text-xl font-bold',
                  metrics.engagement.oneOnOneCoverage >= 80 ? 'text-green-500' :
                  metrics.engagement.oneOnOneCoverage >= 50 ? 'text-yellow-500' : 'text-red-500'
                )}>
                  {Math.round(metrics.engagement.oneOnOneCoverage)}%
                </span>
              </div>
              <div className="text-sm text-brand-gray">
                ({Math.round(metrics.engagement.oneOnOneCoverage / 100 * metrics.teamSize.total)} de {metrics.teamSize.total} com 1-on-1s regulares)
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-brand-gray/20">
                <span className="text-brand-gray">Saúde Geral</span>
                <EngagementIndicator signal={metrics.engagement.overallHealth} showLabel />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Direct Reports */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Reportes Diretos ({directReports.length})</CardTitle>
          <div className="flex items-center gap-2">
            {/* Sort dropdown */}
            <select
              value={directReportsSortBy}
              onChange={(e) => handleSortChange(e.target.value as DirectReportSortBy)}
              className="text-sm bg-brand-dark-light border border-brand-gray/30 rounded px-2 py-1 text-white"
            >
              <option value="score">Por Score</option>
              <option value="name">Por Nome</option>
              <option value="teamSize">Por Tamanho da Equipe</option>
              <option value="engagement">Por Engajamento</option>
            </select>
            <button
              onClick={() => navigate({
                to: '/performance/radar/$employeeId/tree',
                params: { employeeId }
              })}
              className="flex items-center gap-1 text-sm text-brand-primary hover:underline"
            >
              <Network className="w-4 h-4" />
              Ver Árvore Completa
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-brand-gray/20">
            {sortedReports.map((report) => (
              <div key={report.employee.employeeId} className="py-2 first:pt-0 last:pb-0">
                <RadarCard
                  employee={report.employee}
                  performanceScore={report.performanceScore}
                  teamSize={report.teamSize}
                  engagementSignal={report.engagementSignal}
                  variant="compact"
                  onClick={() => navigate({
                    to: '/performance/radar/$employeeId',
                    params: { employeeId: report.employee.employeeId }
                  })}
                  isFavorite={isFavorite(report.employee.employeeId)}
                  onFavorite={() => toggleFavorite({
                    employeeId: report.employee.employeeId,
                    name: report.employee.name,
                    pictureUrl: report.employee.pictureUrl ?? undefined,
                  })}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
